/**
 * 处理滚动加载逻辑
 */
import omit from "omit.js"
import React, { useRef, useEffect, useCallback } from 'react';
import CONFIG from './config';
import { getSumHeight } from './utils/util';
import { debounce } from "../../wui-core/src/browserUtils";
import { FlatTreeData, InfiniteScrollProps } from './iTree'

const defaultProps: Partial<InfiniteScrollProps> = {
    element: 'div',
    style: {},
    treeList: [],
    handleScroll: () => {}
};

const InfiniteScroll: React.FC<InfiniteScrollProps> = (props) => {
    const mergedProps = { ...defaultProps, ...props };

    // 使用 useRef 存储组件实例变量
    const scrollComponentRef = useRef<HTMLElement | undefined>(undefined);
    const scrollTopRef = useRef<number>(0);
    const rowsInViewRef = useRef<number>(CONFIG.defaultRowsInView);
    const treeListRef = useRef<any>(mergedProps.treeList);
    const loadCountRef = useRef<number>(
        (mergedProps.loadBuffer || CONFIG.loadBuffer)
            ? CONFIG.defaultRowsInView + (mergedProps.loadBuffer ?? CONFIG.loadBuffer) * 2
            : 16
    );
    const currentIndexRef = useRef<number>(0);
    const startIndexRef = useRef<number>(0);
    const endIndexRef = useRef<number>(loadCountRef.current);
    const optionsRef = useRef<any>(null);

    // 检测是否支持 passive listener
    const isPassiveSupported = useCallback(() => {
        let passive = false;
        const testOptions = {
            get passive() {
                passive = true;
                return '';
            }
        };
        try {
            // @ts-ignore 仅用于测试是否可以使用 passive listener
            document.addEventListener('test', null, testOptions);
            // @ts-ignore 仅用于测试是否可以使用 passive listener
            document.removeEventListener('test', null, testOptions);
        } catch (e) {
            // ignore
        }
        return passive;
    }, []);

    // 生成事件监听选项
    const eventListenerOptions = useCallback(() => {
        let options: any = mergedProps.useCapture;

        if (isPassiveSupported()) {
            options = {
                useCapture: mergedProps.useCapture,
                passive: true
            };
        }
        return options;
    }, [mergedProps.useCapture, isPassiveSupported]);

    /**
     * 获取父组件(用户自定义父组件或者当前dom的parentNode)
     */
    const getParentElement = useCallback((el: HTMLElement | undefined): HTMLElement => {
        const scrollParent = mergedProps.getScrollParent && mergedProps.getScrollParent();
        if (scrollParent != null) {
            return scrollParent;
        }
        return (el && el.parentNode) as HTMLElement;
    }, [mergedProps.getScrollParent]);

    /**
     * 根据 startIndex 和 endIndex 截取数据
     */
    const sliceTreeList = useCallback((startIndex: number, endIndex: number) => {
        let newTreeList = treeListRef.current.slice(startIndex, endIndex);
        mergedProps.handleScroll && mergedProps.handleScroll(newTreeList, startIndex, endIndex);
    }, [mergedProps.handleScroll]);

    /**
     * @description 根据返回的scrollTop计算当前的索引。
     */
    const handleScrollY = useCallback((isNewDataLess?: boolean, isFold?: boolean) => {
        const { store } = mergedProps;
        const parentElement = getParentElement(scrollComponentRef.current);
        if (!parentElement) {
            return;
        }
        let scrollEl = parentElement;
        let scrollY = scrollEl && scrollEl.clientHeight;
        let scrollTop = scrollEl && scrollEl.scrollTop;
        let rowHeight = store.getState().rowHeight;
        // 默认显示20条，rowsInView根据定高算的。在非固定高下，这个只是一个大概的值。
        rowsInViewRef.current = scrollY ? Math.floor(scrollY / rowHeight) : CONFIG.defaultRowsInView;
        let currentIndex = currentIndexRef.current,
            startIndex = startIndexRef.current,
            endIndex = endIndexRef.current,
            treeList = treeListRef.current,
            loadCount = loadCountRef.current,
            rowsInView = rowsInViewRef.current,
            loadBuffer = mergedProps.loadBuffer ?? CONFIG.loadBuffer;
        let index = 0;
        let tempScrollTop = scrollTopRef.current;
        // 根据 scrollTop 计算 currentIndex
        while (tempScrollTop > 0) {
            tempScrollTop -= rowHeight;
            if (tempScrollTop > 0) {
                index += 1;
            }
        }
        if (isNewDataLess && !isFold) {
            parentElement.scrollTop = 0 // 如果不是因为点击收起造成的数据减少，则滚动条回到顶部
        }
        // true 为向下滚动， false 为向上滚动
        let isScrollDown = index - currentIndex > 0;

        if (index < 0) index = 0;
        // 如果之前的索引和下一次的不一样则重置索引和滚动的位置
        currentIndexRef.current = currentIndex !== index ? index : currentIndex;

        // 如果rowsInView 小于 缓存的数据则重新render
        // 向下滚动 下临界值超出缓存的endIndex则重新渲染
        if (isScrollDown && rowsInView + index > endIndex - CONFIG.rowDiff) {
            startIndex = index - loadBuffer > 0 ? index - loadBuffer : 0;
            endIndex = startIndex + loadCount;
            if (endIndex > treeList.length) {
                endIndex = treeList.length;
            } else {
                const rowHeight: number = store.getState().rowHeight
                const preHeight = getSumHeight(0, startIndex, rowHeight);
                const sufHeight = getSumHeight(endIndex, treeList.length, rowHeight);
                if (scrollTop > preHeight + sufHeight) { // 不够再滚动一次了，直接渲染到最后一个节点
                    endIndex = treeList.length;
                }
            }

            if (endIndex > endIndexRef.current) {
                startIndexRef.current = startIndex;
                endIndexRef.current = endIndex;
                sliceTreeList(startIndexRef.current, endIndexRef.current);
            }
        }
        // 向上滚动，当前的index是否已经加载（currentIndex），若干上临界值小于startIndex则重新渲染
        if (!isScrollDown && index < startIndex + CONFIG.rowDiff) {
            startIndex = index - loadBuffer;
            if (startIndex < 0) {
                startIndex = 0;
            }
            if (startIndex <= startIndexRef.current) {
                startIndexRef.current = startIndex;
                endIndexRef.current = startIndexRef.current + loadCount;
                sliceTreeList(startIndexRef.current, endIndexRef.current);
            }
        }
    }, [mergedProps.store.getState().rowHeight, getParentElement, sliceTreeList, mergedProps.loadBuffer]);

    /**
     * 滚动事件监听
     */
    const scrollListener = useCallback(() => {
        const { height } = mergedProps;
        const el = scrollComponentRef.current!;
        const parentNode = height ? el : getParentElement(el);
        scrollTopRef.current = parentNode?.scrollTop || 0;
        handleScrollY();
    }, [mergedProps.height, getParentElement, handleScrollY]);

    // 使用 useRef 保存防抖函数的引用
    // 滚动或resize事件处理器
    const scrollOrResizeHandler = useCallback(() => {
        const { debounceDuration } = mergedProps;
        debounce(scrollListener(), (debounceDuration ?? 150), false);
    }, [mergedProps.debounceDuration, scrollListener]);

    /**
     * 解除scroll事件监听
     */
    const detachScrollListener = useCallback(() => {
        let scrollEl: HTMLElement | (Window & typeof globalThis) = window;
        if (mergedProps.useWindow === false) {
            scrollEl = getParentElement(scrollComponentRef.current);
        }

        scrollEl.removeEventListener(
            'scroll',
            scrollOrResizeHandler,
            optionsRef.current ? optionsRef.current : mergedProps.useCapture
        );
        scrollEl.removeEventListener(
            'resize',
            scrollOrResizeHandler,
            optionsRef.current ? optionsRef.current : mergedProps.useCapture
        );
    }, [mergedProps.useWindow, mergedProps.useCapture, getParentElement, scrollOrResizeHandler]);

    /**
     * 绑定scroll事件
     */
    const attachScrollListener = useCallback(() => {
        const { store, height } = mergedProps;
        let parentElement = height ? scrollComponentRef.current : getParentElement(scrollComponentRef.current);
        if (!parentElement) {
            return;
        }
        let scrollEl = parentElement;
        let scrollY = scrollEl && scrollEl.clientHeight;

        let rowHeight = store.getState().rowHeight;
        // 默认显示20条，rowsInView根据定高算的。
        rowsInViewRef.current = scrollY ? Math.floor(scrollY / rowHeight) : CONFIG.defaultRowsInView;
        scrollEl.addEventListener('scroll', scrollOrResizeHandler,
            optionsRef.current ? optionsRef.current : mergedProps.useCapture
        );
        scrollEl.addEventListener('resize', scrollOrResizeHandler,
            optionsRef.current ? optionsRef.current : mergedProps.useCapture
        );
    }, [mergedProps, getParentElement, scrollOrResizeHandler]);

    // 更新 treeList 和 loadCount
    useEffect(() => {
        const prevTreeList = treeListRef.current;
        if (mergedProps.treeList !== prevTreeList) {
            const isNewDataLess = (mergedProps.treeList as FlatTreeData[]).length < (prevTreeList as FlatTreeData[]).length;
            treeListRef.current = mergedProps.treeList;
            handleScrollY(isNewDataLess, mergedProps.isFold);
        }
    }, [mergedProps.treeList, mergedProps.isFold, handleScrollY]);

    // 初始化时设置选项并绑定事件
    useEffect(() => {
        optionsRef.current = eventListenerOptions();
        attachScrollListener();

        return () => {
            detachScrollListener();
        };
    }, []);

    // 组合ref回调
    const setRef = useCallback((node: HTMLElement) => {
        scrollComponentRef.current = node;
        if (mergedProps.ref) {
            if (typeof mergedProps.ref === 'function') {
                mergedProps.ref(node);
            } else {
                (mergedProps.ref as any).current = node;
            }
        }
    }, [mergedProps.ref]);

    const {
        children,
        element,
        height,
        style: styleRoot,
        ref,
        ...restProps
    } = mergedProps;

    const style = height ? {...styleRoot, height: `${height}px`, overflowY: 'scroll' as const} : styleRoot;
    const childrenArray = [children];

    return React.createElement(
        (element as string),
        {
            ...omit(restProps, ["debounceDuration", "isFold", "getScrollParent", "treeList", "handleScroll", "store"]),
            style,
            ref: setRef
        },
        childrenArray
    );
};

export default InfiniteScroll;
